import { TRACKING_EVENT_NAME } from '@adtrackify/at-tracking-event-types';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useAddPaymentMethod } from '../../hooks/use-add-payment-method';

import { BUTTON_SIZE, BUTTON_VARIANT } from '@/components/ui/types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { CALLOUT_EMPHASIS, CALLOUT_SIZE, CALLOUT_TONE } from '@/components/ui/types/callout-variants-enums';
import { Callout } from '@/components/ui/callout';
import { DIALOG_DISMISSAL, DIALOG_SIZE } from '@/components/ui/types/dialog-variants-enums';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RESULT_STATE_PLACEMENT, RESULT_STATE_SIZE, RESULT_STATE_TONE } from '@/components/ui/types/result-state-variants-enums';
import { ResultState } from '@/components/ui/result-state';
import { LoadingRegion } from '@/components/ui/loading-region';
import { SPINNER_SIZE } from '@/components/ui/types/spinner-variants-enums';
import { Spinner } from '@/components/ui/spinner';
import { useAnalytics } from '@/hooks/analytics/use-analytics';
import { getStripeElementsAppearance, getStripeElementsFonts } from '@/features/billing/helpers/stripe/stripe-elements-appearance';
import { STRIPE_PAYMENT_ELEMENT_OPTIONS } from '@/features/billing/constants/stripe-elements-appearance-constants';
import { STRIPE_ELEMENTS_SURFACE } from '@/features/billing/types/stripe-elements-appearance-enums';
import { useAccountState } from '@/contexts/account/account-context';
import type { CompleteStripeSetupIntentResponse } from '@/services/internal-api/subscriptions/types/subscription-api-service-types';
import * as Logger from '@/lib/monitoring/logger';
import { isAddPaymentMethodSubmitBusy } from '../../helpers/is-add-payment-method-submit-busy';

interface AddPaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (response: CompleteStripeSetupIntentResponse) => void;
  makeDefaultPaymentMethod?: boolean;
  description?: string;
  submitLabel?: string;
  analyticsFlow?: string;
}

const DEFAULT_DESCRIPTION = 'Add a new payment method to your account.';
const DEFAULT_SUBMIT_LABEL = 'Add payment method';
const DEFAULT_ANALYTICS_FLOW = 'billing_settings';
const ADD_PAYMENT_METHOD_FORM_ID = 'add-payment-method-form';

function hasStripePublishableKey(): boolean {
  return Boolean(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
}

export function AddPaymentMethodModal({
  open,
  onOpenChange,
  onSuccess: onSuccessCallback,
  makeDefaultPaymentMethod,
  description = DEFAULT_DESCRIPTION,
  submitLabel = DEFAULT_SUBMIT_LABEL,
  analyticsFlow = DEFAULT_ANALYTICS_FLOW,
}: AddPaymentMethodModalProps) {
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const stripeConfigured = hasStripePublishableKey();
  const {
    clientSecret,
    isCreatingSetupIntent,
    createSetupIntentError,
    createSetupIntent,
    completeSetupIntent,
    isCompletingSetupIntent,
    completeSetupIntentError,
    reset,
  } = useAddPaymentMethod();

  useEffect(() => {
    if (!open || !stripeConfigured || clientSecret || isCreatingSetupIntent) {
      return;
    }
    void createSetupIntent();
  }, [open, stripeConfigured, clientSecret, isCreatingSetupIntent, createSetupIntent]);

  useEffect(() => {
    if (!open || stripePromise || !stripeConfigured) {
      return;
    }

    setStripePromise(loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY));
  }, [open, stripePromise, stripeConfigured]);

  const isBusy = isAddPaymentMethodSubmitBusy({
    isCreatingSetupIntent,
    isCompletingSetupIntent,
    isProcessing,
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && isBusy) {
      return;
    }
    if (!isOpen) {
      reset();
      setIsProcessing(false);
    }
    onOpenChange(isOpen);
  };

  const handleSuccess = (response: CompleteStripeSetupIntentResponse) => {
    setIsProcessing(false);
    onSuccessCallback?.(response);
    reset();
    onOpenChange(false);
  };

  const showPaymentForm =
    stripeConfigured &&
    Boolean(clientSecret) &&
    Boolean(stripePromise) &&
    !isCreatingSetupIntent &&
    !createSetupIntentError;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size={DIALOG_SIZE.SM}
        dismissal={isBusy ? DIALOG_DISMISSAL.BLOCKED : DIALOG_DISMISSAL.STANDARD}
        busy={isBusy}
      >
        <DialogHeader>
          <DialogTitle className="type-dialog-title">Add payment method</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!stripeConfigured && (
          <>
            <DialogBody>
              <ResultState.Root
                placement={RESULT_STATE_PLACEMENT.SECTION}
                role="alert"
                size={RESULT_STATE_SIZE.COMPACT}
              >
                <ResultState.Icon tone={RESULT_STATE_TONE.ERROR}>
                  <AlertCircle />
                </ResultState.Icon>
                <ResultState.Content>
                  <ResultState.Title>Payment form unavailable</ResultState.Title>
                  <ResultState.Description>
                    Stripe is not configured for this environment. Contact support if you need to
                    add a payment method.
                  </ResultState.Description>
                </ResultState.Content>
              </ResultState.Root>
            </DialogBody>
            <DialogFooter>
              <Button
                variant={BUTTON_VARIANT.SECONDARY}
                size={BUTTON_SIZE.DEFAULT}
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        )}

        {stripeConfigured && isCreatingSetupIntent && (
          <DialogBody>
            <LoadingRegion
              label="Setting up payment form…"
              className="flex flex-col items-center justify-center py-6"
            >
              <Spinner size={SPINNER_SIZE.LG} className="mb-4" />
              <p className="text-sm text-content-secondary" aria-hidden="true">
                Setting up payment form…
              </p>
            </LoadingRegion>
          </DialogBody>
        )}

        {stripeConfigured && createSetupIntentError && (
          <>
            <DialogBody>
              <ResultState.Root
                placement={RESULT_STATE_PLACEMENT.SECTION}
                role="alert"
                size={RESULT_STATE_SIZE.COMPACT}
              >
                <ResultState.Icon tone={RESULT_STATE_TONE.ERROR}>
                  <AlertCircle />
                </ResultState.Icon>
                <ResultState.Content>
                  <ResultState.Title>Unable to set up payment form</ResultState.Title>
                  <ResultState.Description>{createSetupIntentError}</ResultState.Description>
                </ResultState.Content>
                <ResultState.Actions>
                  <Button
                    variant={BUTTON_VARIANT.SECONDARY}
                    size={BUTTON_SIZE.COMPACT}
                    onClick={() => {
                      void createSetupIntent();
                    }}
                  >
                    Try Again
                  </Button>
                </ResultState.Actions>
              </ResultState.Root>
            </DialogBody>
            <DialogFooter>
              <Button
                variant={BUTTON_VARIANT.SECONDARY}
                size={BUTTON_SIZE.DEFAULT}
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        )}

        {stripeConfigured &&
          clientSecret &&
          !stripePromise &&
          !isCreatingSetupIntent &&
          !createSetupIntentError && (
            <DialogBody>
              <LoadingRegion
                label="Loading payment form…"
                className="flex flex-col items-center justify-center py-6"
              >
                <Spinner size={SPINNER_SIZE.LG} className="mb-4" />
                <p className="text-sm text-content-secondary" aria-hidden="true">
                  Loading payment form…
                </p>
              </LoadingRegion>
            </DialogBody>
          )}

        {showPaymentForm && clientSecret && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: getStripeElementsAppearance(STRIPE_ELEMENTS_SURFACE.OPERATIONAL),
              fonts: getStripeElementsFonts(STRIPE_ELEMENTS_SURFACE.OPERATIONAL),
            }}
          >
            <PaymentForm
              onSuccess={handleSuccess}
              completeSetupIntent={completeSetupIntent}
              isCompletingSetupIntent={isCompletingSetupIntent}
              completeSetupIntentError={completeSetupIntentError}
              onCancel={() => handleOpenChange(false)}
              onProcessingChange={setIsProcessing}
              makeDefaultPaymentMethod={makeDefaultPaymentMethod}
              submitLabel={submitLabel}
              analyticsFlow={analyticsFlow}
            />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface PaymentFormProps {
  onSuccess: (response: CompleteStripeSetupIntentResponse) => void;
  onCancel: () => void;
  onProcessingChange: (isProcessing: boolean) => void;
  completeSetupIntent: (
    paymentMethodId: string,
    options?: { makeDefaultPaymentMethod?: boolean }
  ) => Promise<CompleteStripeSetupIntentResponse>;
  isCompletingSetupIntent: boolean;
  completeSetupIntentError: string | null;
  makeDefaultPaymentMethod?: boolean;
  submitLabel: string;
  analyticsFlow: string;
}

function PaymentForm({
  onSuccess,
  onCancel,
  onProcessingChange,
  completeSetupIntent,
  isCompletingSetupIntent,
  completeSetupIntentError,
  makeDefaultPaymentMethod,
  submitLabel,
  analyticsFlow,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { track } = useAnalytics();
  const { accountId, currentAccount } = useAccountState();

  const subscription = currentAccount?.subscription;
  const plan = subscription?.subscriptionPlan;

  const setProcessing = (processing: boolean) => {
    setIsProcessing(processing);
    onProcessingChange(processing);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (result.error) {
        setErrorMessage(result.error.message || 'An error occurred while processing your payment');

        track(TRACKING_EVENT_NAME.PAYMENT_FAILED, {
          accountId,
          subscriptionId: subscription?.id,
          planId: plan?.id,
          planName: plan?.displayName,
          billingInterval: plan?.billingFrequency,
          value: plan?.price,
          currency: 'USD',
          failureReason: result.error.message,
          flow: analyticsFlow,
          items: plan
            ? [
                {
                  id: plan.id,
                  name: plan.displayName,
                  price: plan.price,
                  quantity: 1,
                  category: 'subscription',
                },
              ]
            : [],
        });
        return;
      }

      if (result.setupIntent) {
        const { payment_method } = result.setupIntent;
        const paymentMethodId =
          typeof payment_method === 'string' ? payment_method : payment_method?.id;

        if (!paymentMethodId) {
          setErrorMessage('Failed to retrieve payment method information');
          return;
        }

        try {
          const response = await completeSetupIntent(
            paymentMethodId,
            makeDefaultPaymentMethod !== undefined ? { makeDefaultPaymentMethod } : undefined
          );

          onSuccess(response);
        } catch (error) {
          Logger.debug('Complete setup intent failed; handled by hook', { error });
          // Error is handled by the hook and displayed via completeSetupIntentError
        }
      }
    } catch (caughtError) {
      Logger.error('Add payment method submission failed', caughtError instanceof Error ? caughtError : null);
      setErrorMessage('An unexpected error occurred');
    } finally {
      setProcessing(false);
    }
  };

  const isSubmitting = isProcessing || isCompletingSetupIntent;
  const displayError = errorMessage || completeSetupIntentError;

  return (
    <>
      <DialogBody className="space-y-4">
        <form id={ADD_PAYMENT_METHOD_FORM_ID} onSubmit={e => { void handleSubmit(e); }}>
          <PaymentElement options={STRIPE_PAYMENT_ELEMENT_OPTIONS} />
        </form>

        {displayError ? (
          <Callout.Root
            emphasis={CALLOUT_EMPHASIS.SUBTLE}
            role="alert"
            size={CALLOUT_SIZE.COMPACT}
            tone={CALLOUT_TONE.DANGER}
          >
            <Callout.Icon>
              <AlertCircle />
            </Callout.Icon>
            <Callout.Content>
              <Callout.Description>{displayError}</Callout.Description>
            </Callout.Content>
          </Callout.Root>
        ) : null}
      </DialogBody>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          type="button"
          variant={BUTTON_VARIANT.SECONDARY}
          size={BUTTON_SIZE.DEFAULT}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form={ADD_PAYMENT_METHOD_FORM_ID}
          size={BUTTON_SIZE.DEFAULT}
          variant={BUTTON_VARIANT.PRIMARY}
          disabled={!stripe || isSubmitting}
          isLoading={isSubmitting}
          loadingLabel={isCompletingSetupIntent ? 'Saving…' : 'Processing…'}
        >
          {submitLabel}
        </Button>
      </DialogFooter>
    </>
  );
}
